import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { CircularProgress, Box } from '@mui/material';
import { resolvePostLoginPath } from '../utils/safeRedirect';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireAdmin = false,
  redirectTo = '/bejelentkezes',
}) => {
  const location = useLocation();
  const { isAuthenticated, user, loading, token } = useSelector((state: RootState) => state.auth);
  const isAdmin = !!(user && (user.is_staff || user.is_superuser));
  // Hard refresh: the token is restored from sessionStorage before
  // getCurrentUser resolves, so `user` is briefly null while a token exists.
  // Without this, admin routes bounce to "/" on reload.
  const userStillLoading = !!token && !user;

  if (loading || userStillLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // Save the attempted location for redirecting after login
    return (
      <Navigate
        to={redirectTo}
        state={{
          from: location,
          message: 'A funkció használatához be kell jelentkezned.'
        }}
        replace
      />
    );
  }

  // Vendég-útvonal (belépés, regisztráció) belépett felhasználónál: a biztonságos ?next= /
  // state.from célra (pl. /csatlakozas/<kód>), különben a főoldalra.
  if (!requireAuth && isAuthenticated) {
    return <Navigate to={resolvePostLoginPath(location.state, location.search)} replace />;
  }

  // Admin-only route — non-admin authenticated users bounced home so
  // they don't trigger a wave of failed admin-endpoint requests.
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Render the protected component
  return <>{children}</>;
};

export default ProtectedRoute;